/**
 * The realtime connection.
 *
 * One socket per tab, authenticated at the handshake. Reconnection is not a
 * special case here: state lives in PostgreSQL, so coming back means fetching
 * the session and replaying the events since the last sequence number we saw.
 * The legacy client had a `restore_data` / `restore_trigger` dance against an
 * in-process dict, which lost everything whenever the server restarted — which
 * it did on every print.
 */

import { io, type Socket } from 'socket.io-client'

let socket: Socket | null = null
let connectionListeners: ((state: ConnectionState) => void)[] = []

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected'

let currentState: ConnectionState = 'disconnected'

function setState(next: ConnectionState) {
  if (currentState === next) return
  currentState = next
  connectionListeners.forEach((fn) => fn(next))
}

export function getConnectionState() {
  return currentState
}

export function onConnectionChange(fn: (state: ConnectionState) => void) {
  connectionListeners.push(fn)
  fn(currentState)
  return () => {
    connectionListeners = connectionListeners.filter((f) => f !== fn)
  }
}

export interface ConnectOptions {
  /** Access token for a person. Omit when the cookie is enough. */
  token?: string
  /** Device token for a tablet. */
  deviceToken?: string
}

export function connectSocket(options: ConnectOptions = {}): Socket {
  if (socket?.connected) return socket

  socket?.disconnect()
  setState('connecting')

  socket = io({
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    withCredentials: true,
    auth: {
      ...(options.token ? { token: options.token } : {}),
      ...(options.deviceToken ? { device_token: options.deviceToken } : {}),
    },
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    // Keep trying. A tablet in a hotel lobby loses wifi for a minute at a
    // time, and giving up would leave a guest staring at a dead screen.
    reconnectionAttempts: Infinity,
  })

  socket.on('connect', () => {
    setState('connected')
    // A reconnect is a new socket id, and room membership does not survive it.
    rejoinRooms()
  })
  socket.on('disconnect', () => setState('disconnected'))
  socket.io.on('reconnect_attempt', () => setState('reconnecting'))
  socket.io.on('reconnect', () => setState('connected'))
  socket.on('connect_error', () => setState('reconnecting'))

  return socket
}

export function getSocket(): Socket | null {
  return socket
}

export function disconnectSocket() {
  socket?.disconnect()
  socket = null
  setState('disconnected')
}

/** Subscribe to a server event. Returns an unsubscribe function. */
export function on<T>(event: string, handler: (payload: T) => void): () => void {
  const active = socket
  if (!active) return () => undefined
  active.on(event, handler as (...args: unknown[]) => void)
  return () => {
    active.off(event, handler as (...args: unknown[]) => void)
  }
}

/**
 * Emit with an acknowledgement, promisified, with a timeout that actually fires.
 *
 * A refusal is a rejection. Every server handler answers a Forbidden, a
 * validation failure or an illegal transition with `{ok: false, code, error}`
 * in the *acknowledgement*, so resolving on any ack meant `.catch()` never ran
 * and the refusal was read by nobody: the tablet showed the guest's corrected
 * surname, the database kept the old one, and the old one is what was stamped
 * onto the signed PDF.
 */
export class AckError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'AckError'
  }
}

export function emit<T = unknown>(
  event: string,
  payload: Record<string, unknown>,
  timeoutMs = 5000,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const active = socket
    if (!active?.connected) {
      reject(new AckError('Not connected', 'offline'))
      return
    }
    const timer = setTimeout(() => reject(new Error(`${event} timed out`)), timeoutMs)
    active.emit(event, payload, (response: T) => {
      clearTimeout(timer)
      const ack = response as { ok?: boolean; code?: string; error?: string } | null
      if (ack && typeof ack === 'object' && ack.ok === false) {
        reject(new AckError(ack.error ?? `${event} was refused`, ack.code))
        return
      }
      resolve(response)
    })
  })
}

/**
 * Join a session room and replay anything missed.
 *
 * `sinceSeq` is the whole reconnection contract: the server returns the
 * current state plus every event after that sequence number, so a tablet that
 * was asleep for two minutes catches up exactly.
 *
 * Membership is per-socket-id, and the server's own `connect` handler cannot
 * restore it — it re-enters the property, desk and device rooms because it
 * knows those from the handshake, but it has no idea which session this client
 * was watching. So we remember, and re-join ourselves. Relying on the window's
 * `online` event was not enough: it fires when the OS network interface flips,
 * not when a websocket ping times out, a proxy drops the connection or the
 * server restarts — all of which reconnect with a new socket id and left the
 * tablet silently outside the room, deaf to every correction the desk made.
 */
const joined = new Map<string, number>()

export async function joinSession(sessionId: string, sinceSeq = 0) {
  joined.set(sessionId, sinceSeq)
  return emit<{
    ok: boolean
    session?: unknown
    replay?: { seq: number; event_type: string; payload: Record<string, unknown> }[]
    error?: string
  }>('session.join', { session_id: sessionId, since_seq: sinceSeq })
}

export function leaveSession(sessionId: string) {
  joined.delete(sessionId)
  socket?.emit('session.leave', { session_id: sessionId })
}

/** Remember how far a room has been caught up, so a re-join replays the rest. */
export function noteSeq(sessionId: string, seq: number) {
  if (joined.has(sessionId)) joined.set(sessionId, seq)
}

/** Run after every (re)connect. Exported for the tests. */
export function rejoinRooms() {
  for (const [sessionId, seq] of joined) {
    void emit('session.join', { session_id: sessionId, since_seq: seq }).catch(() => undefined)
  }
}

/**
 * Coalesce rapid field edits.
 *
 * A guest typing a surname produces a keystroke every ~80 ms. Without this the
 * channel carries eight messages per second per field; with it, the desk sees
 * a settled value and the tablet stays responsive.
 *
 * `keyOf` gives each field its own timer. One shared timer meant an edit to
 * the second field cancelled the pending write of the first, and the first
 * field's value was never sent at all.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms = 150,
  keyOf: (...args: A) => string = () => '',
) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  return (...args: A) => {
    const key = keyOf(...args)
    clearTimeout(timers.get(key))
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key)
        fn(...args)
      }, ms),
    )
  }
}
